<template>
  <BaseWidget
    class="heatmap-table"
    :title="content.title"
    :widgetId="content.id"
    :edit="edit"
    :content="content"
    :isWidgetLoading="isWidgetLoading"
    :getWidgetReport="getWidgetReport"
  >
    <template v-slot:widget-control>
      <div class="widget-controls">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              height="48"
              @click="showColumnDialog = !showColumnDialog"
              class="elevation-0 show-columns-btn action-icon"
              v-bind="attrs"
              v-on="on"
            >
              <template v-slot:default>
                <span class="custom-loader">
                  <v-icon light>mdi-format-columns</v-icon>
                </span>
              </template>
            </v-btn>
          </template>
          <span>{{ Lang.showColumns }}</span>
        </v-tooltip>
      </div>
    </template>
    <div class="table-container table-widget-container no-row-hover">
      <v-simple-table fixed-header :style="{ '--hm-cols': totalDataCols }">
        <template v-slot:default>
          <colgroup>
            <col class="hm-col-first" />
            <col v-for="h in dataHeaders" :key="h.value" class="hm-col-auto" />
          </colgroup>
          <thead class="v-data-table-header">
            <tr>
              <th class="heatmap-table__th heatmap-table__th-align-left" scope="col" rowspan="2">Channel</th>
              <th
                v-for="grp in headerGroups"
                :key="grp.key"
                class="heatmap-table__th heatmap-table__group"
                :colspan="grp.cols.length"
                scope="colgroup"
              >
                {{ grp.title }}
              </th>
            </tr>

            <tr>
              <th
                v-for="(sub, i) in subHeaders"
                :key="sub.value"
                class="heatmap-table__th heatmap-table__th-sub"
                :class="[
                  isDisplayMetric(sub.value) ? 'heatmap-table__th-align-right' : 'heatmap-table__th-align-left',
                  isGroupStartByIndex(i) && 'hm-col-start',
                  isGroupEndByIndex(i) && 'hm-col-end',
                ]"
                scope="col"
              >
                {{ sub.text }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="row in data" :key="row.metric">
              <td class="heatmap-table__th-align-left">{{ row.metric }}</td>
              <td
                v-for="(h, i) in dataHeaders"
                :key="h.value"
                :class="[
                  isDisplayMetric(h.value) ? 'cell-display-metrics heatmap-table__th-align-right' : '',
                  isGroupStartByIndex(i) && 'hm-col-start',
                  isGroupEndByIndex(i) && 'hm-col-end',
                ]"
                :style="[h.width ? { width: h.width } : null, cellStyle(h.value, row[h.value])]"
              >
                {{ formatCell(h.value, row[h.value]) }}
              </td>
            </tr>
            <tr v-if="dataHeaders.length" class="hm-summary-row">
              <td class="heatmap-table__th-align-left hm-summary-label">{{ summaryRow.metric || Lang.total }}</td>
              <td
                v-for="(h, i) in dataHeaders"
                :key="`summary_${h.value}`"
                :class="[
                  isDisplayMetric(h.value) ? 'cell-display-metrics heatmap-table__th-align-right' : '',
                  isGroupStartByIndex(i) && 'hm-col-start',
                  isGroupEndByIndex(i) && 'hm-col-end',
                ]"
                :style="[h.width ? { width: h.width } : null]"
              >
                {{ formatCell(h.value, summaryRow[h.value]) }}
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </div>
    <HeatmapTableColumnsDrawer
      v-model="showColumnDialog"
      :selected-values="selectedMetricValues"
      :required-values="heatmapRequiredValues"
      @columnsUpdate="onColumnsUpdate"
    />
  </BaseWidget>
</template>
<script lang="ts">
import { cloneDeep } from "lodash";
import Vue from "vue";
import { DataOptions } from "vuetify";
import { ThemeOptions } from "vuetify/types/services/theme";
import { mapGetters } from "vuex";

import HeatmapTableColumnsDrawer from "@/components/drawers/HeatmapTableColumnsDrawer.vue";
import BaseWidget from "@/components/widgets/explore/BaseWidget.vue";
import { weightedMap } from "@/constants";
import {
  HEATMAP_BASE_METRIC_SUFFIXES,
  HEATMAP_DEFAULT_SELECTED_METRICS,
  HEATMAP_REQUIRED_METRIC,
} from "@/constants/heatmapTable";
import csvExport, { buildFileName, buildHeaderRows } from "@/helpers/csvExport";
import Lang from "@/lang";
import inputsDictionary from "@/settings/planInputsDictionary";
import { tableRows } from "@/settings/tableSettings";
import { TableDataRow } from "@/types/charts";
import { PlanInputDictionary } from "@/types/common";
import { ContentWidget } from "@/types/layouts";
import { Arr, OutputRecord, Project } from "@/types/project";

function scenarioKey(name: string) {
  return `scenario_${slugify(name)}`;
}

export type TableHeader = {
  text: string;
  value: string;
  width?: string;
  cellClass?: string;
};

export type HeaderGroup = {
  key: string;
  title: string;
  cols: TableHeader[];
};

function slugify(str: string): string {
  return (str || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

type HeatmapOutputRow = OutputRecord & {
  channel_budget?: number | string | null;
  channel_percentage?: number | string | null;
  channel_name?: string | null;
  Channel?: string | null;
  flights?: Array<Record<string, unknown>>;
};

export default Vue.extend({
  name: "HeatmapTableWidget",

  props: {
    content: Object as () => ContentWidget,
    edit: Boolean,
  },

  components: { HeatmapTableColumnsDrawer, BaseWidget },

  watch: {
    projectFiltered: {
      immediate: true,
      handler() {
        this.rebuild();
      },
    },
  },

  async beforeMount() {
    this.theme = this.$vuetify.theme;
  },

  data() {
    return {
      Lang,
      tableRows,
      options: {} as DataOptions,
      isWidgetLoading: false,
      data: [] as TableDataRow[],
      headerLabels: [] as TableHeader[],
      showColumnDialog: false,
      theme: {} as ThemeOptions,
      displayMetrics: inputsDictionary.displayMetrics,
      heatmapTableMetrics: inputsDictionary.heatmapTableMetrics as PlanInputDictionary[],
      summaryRow: {} as Record<string, unknown>,
      rowsPerPage: null as null | number,
      footerProps: {
        showCurrentPage: true,
        disableItemsPerPage: true,
        disablePagination: false,
      },
      heatmapRequiredValues: [HEATMAP_REQUIRED_METRIC] as string[],
      selectedMetricValues: [...HEATMAP_DEFAULT_SELECTED_METRICS] as string[],
    };
  },

  computed: {
    ...mapGetters("projectOutput", ["projectFiltered"]),

    dictByValue(): Record<
      string,
      {
        title: string;
        value: string;
        type?: string;
        formatter?: (v: string | number | null) => unknown;
        colors?: unknown;
      }
    > {
      const list = cloneDeep(this.heatmapTableMetrics);
      const map: Record<string, PlanInputDictionary> = {};
      if (Array.isArray(list)) for (const it of list) if (it?.value) map[it.value] = it;
      return map;
    },
    metricSuffixesDesc(): string[] {
      const list = cloneDeep(this.heatmapTableMetrics)
        .map((x: PlanInputDictionary) => x?.value)
        .filter(Boolean);
      const all = Array.from(new Set<string>([...list, ...HEATMAP_BASE_METRIC_SUFFIXES]));
      return all.sort((a, b) => b.length - a.length);
    },

    selectedMetricsSorted(): Array<{ title: string; value: string; type?: string }> {
      const list = cloneDeep(this.heatmapTableMetrics);
      if (!Array.isArray(list)) return [];
      const picked = new Set(this.selectedMetricValues || []);
      return list
        .filter((m) => picked.has(m.value))
        .map((m) => ({
          title: m.title,
          value: m.value,
          type: (m.type || "").toLowerCase(),
        }));
    },

    dataHeaders(): TableHeader[] {
      return (this.headers as TableHeader[]).filter((h) => h.value !== "metric");
    },
    totalDataCols(): number {
      return (this.dataHeaders || []).length;
    },
    headers(): TableHeader[] {
      return this.headerLabels;
    },

    columnMaxByKey(): Record<string, number> {
      const res: Record<string, number> = {};
      const keys = (this.dataHeaders || []).map((h) => h.value);

      for (const k of keys) {
        if (!this.getMetricColorCfgByHeader(k)) continue;

        let max = 0;
        for (const row of this.data) {
          const v = Number(row?.[k] ?? 0);
          if (Number.isFinite(v) && v > max) max = v;
        }
        res[k] = max;
      }
      return res;
    },

    flatHeaders(): TableHeader[] {
      return this.headerLabels || [];
    },

    headerGroups(): HeaderGroup[] {
      const groupsMap = new Map<string, HeaderGroup>();

      for (const h of this.flatHeaders) {
        if (h.value === "metric") continue;
        const { metric, scenarioKey } = this.extractMetricAndScenario(String(h.value));
        if (!metric || !scenarioKey) continue;

        const title = typeof h.text === "string" && h.text.includes("•") ? h.text.split("•")[0].trim() : h.text;
        if (!groupsMap.has(scenarioKey)) groupsMap.set(scenarioKey, { key: scenarioKey, title, cols: [] });
        groupsMap.get(scenarioKey)!.cols.push(h);
      }

      const ordered: string[] = [];
      for (const h of this.flatHeaders) {
        if (h.value === "metric") continue;
        const { scenarioKey } = this.extractMetricAndScenario(String(h.value));
        if (scenarioKey && !ordered.includes(scenarioKey)) ordered.push(scenarioKey);
      }

      return ordered.map((k) => groupsMap.get(k)!).filter(Boolean);
    },

    subHeaders(): Array<{ text: string; value: string; width?: string }> {
      const dict = cloneDeep(this.heatmapTableMetrics);
      const titleByValue: Record<string, string> = {};
      for (const it of dict) if (it && it.value) titleByValue[it.value] = it.title || it.value;

      return this.flatHeaders
        .filter((h) => h.value !== "metric")
        .map((h) => {
          const mk = this.metricFromHeaderKey(h.value);
          const text =
            titleByValue[mk] ||
            (mk === "budget" ? "Budget" : mk === "impressions" ? "Impressions" : mk === "cpm" ? "CPM" : mk);
          return { ...h, text };
        });
    },
  },

  methods: {
    getExportGroupHeaderRow(): string[] {
      const row: string[] = ["Channel"];
      this.headerGroups.forEach((grp) => {
        const repeat = grp.cols.length;
        for (let i = 0; i < repeat; i++) row.push(String(grp.title ?? ""));
      });
      return row;
    },

    getExportSubHeaderRow(): string[] {
      const sub = this.subHeaders.map((h) => String(h.text ?? h.value ?? ""));
      return ["", ...sub];
    },

    applyFormatterForCsv(key: string, raw: unknown): unknown {
      try {
        return this.formatCell(key, raw);
      } catch {
        return raw ?? "";
      }
    },

    buildExportMatrix(): unknown[][] {
      const out: unknown[][] = [];
      out.push(this.getExportGroupHeaderRow());
      out.push(this.getExportSubHeaderRow());

      for (const row of this.data) {
        const csvRow: unknown[] = [];
        csvRow.push(row.metric ?? "");

        this.dataHeaders.forEach((h) => {
          const val = row[h.value];
          csvRow.push(this.applyFormatterForCsv(h.value, val));
        });

        out.push(csvRow);
      }

      if (this.dataHeaders.length) {
        const summaryCsvRow: unknown[] = [];
        summaryCsvRow.push(this.summaryRow.metric ?? Lang.total);

        this.dataHeaders.forEach((h) => {
          const val = this.summaryRow[h.value];
          summaryCsvRow.push(this.applyFormatterForCsv(h.value, val));
        });

        out.push(summaryCsvRow);
      }

      return out;
    },

    getWidgetReport(): void {
      const title = this.content?.title || "Heatmap Table";
      const csvContent: unknown[][] = [];

      csvContent.push(...buildHeaderRows(title));
      csvContent.push([]);

      csvContent.push(...this.buildExportMatrix());

      const fileName = buildFileName(title);
      csvExport(csvContent, fileName);
    },
    groupSize(): number {
      return Math.max(1, this.selectedMetricsSorted.length);
    },

    getMetricColorCfgByMetric(
      metricKey: string
    ): { base: [number, number, number]; aMin: number; aMax: number } | null {
      const rawCfg = this.dictByValue?.[metricKey]?.colors;

      const cfg = rawCfg as
        | Partial<{
            base: unknown;
            aMin: unknown;
            aMax: unknown;
          }>
        | undefined;

      if (
        cfg &&
        Array.isArray(cfg.base) &&
        cfg.base.length === 3 &&
        cfg.base.every((n) => typeof n === "number") &&
        typeof cfg.aMin === "number" &&
        typeof cfg.aMax === "number"
      ) {
        return {
          base: cfg.base as [number, number, number],
          aMin: cfg.aMin,
          aMax: cfg.aMax,
        };
      }

      if (metricKey === "budget") return { base: [149, 103, 217], aMin: 0.2, aMax: 1.0 };
      if (metricKey === "impressions") return { base: [249, 103, 191], aMin: 0.2, aMax: 1.0 };
      if (metricKey === "cpm") return { base: [126, 196, 226], aMin: 0.2, aMax: 1.0 };
      return null;
    },
    getMetricColorCfgByHeader(
      headerKey: string
    ): { base: [number, number, number]; aMin: number; aMax: number } | null {
      const mk = this.metricFromHeaderKey(headerKey);
      return this.getMetricColorCfgByMetric(mk);
    },
    metricFromHeaderKey(key: string): string {
      return this.extractMetricAndScenario(key).metric;
    },
    extractMetricAndScenario(key: string): { metric: string; scenarioKey: string | null } {
      const s = String(key || "");
      for (const m of this.metricSuffixesDesc) {
        const suf = `_${m}`;
        if (s === m) return { metric: m, scenarioKey: null };
        if (s.endsWith(suf)) {
          const scenarioKey = s.slice(0, -suf.length);
          return { metric: m, scenarioKey };
        }
      }
      const i = s.lastIndexOf("_");
      return { metric: i >= 0 ? s.slice(i + 1) : s, scenarioKey: i >= 0 ? s.slice(0, i) : null };
    },
    displayKey(mk: string): string {
      return mk === "budget" ? "channel_budget" : mk;
    },
    onColumnsUpdate(values: string[]) {
      this.selectedMetricValues = values;
      this.rebuild();
    },

    isGroupStartByIndex(i: number): boolean {
      return i % this.groupSize() === 0;
    },
    isGroupEndByIndex(i: number): boolean {
      return i % this.groupSize() === this.groupSize() - 1;
    },

    formatCell(key: string, value: unknown): unknown {
      const mk = this.metricFromHeaderKey(key);
      const can = this.displayKey(mk);
      const dispList = (inputsDictionary.displayMetrics ?? []) as PlanInputDictionary[];
      const heatList = cloneDeep(this.heatmapTableMetrics);

      let fmt: ((v: string | number | null) => unknown) | undefined;

      const disp = dispList.find((x) => x?.value === can);
      if (disp?.formatter && mk !== "roas") fmt = disp.formatter as (v: string | number | null) => unknown;

      if (!fmt) {
        const heat = heatList.find((x) => x?.value === mk);
        if (heat?.formatter) fmt = heat.formatter as (v: string | number | null) => unknown;
      }

      if (typeof fmt === "function") {
        try {
          const safeVal =
            typeof value === "string" || typeof value === "number" ? value : value == null ? null : String(value);
          return fmt(safeVal);
        } catch (e) {
          console.error(e);
        }
      }

      return value;
    },
    cellStyle(key: string, rawValue: string | number): Record<string, string> | null {
      const cfg = this.getMetricColorCfgByHeader(key);
      if (!cfg) return null;

      const v = Number(rawValue ?? 0);
      const max = this.columnMaxByKey[key] ?? 0;

      const [r, g, b] = cfg.base;
      if (!(max > 0)) return { backgroundColor: `rgba(${r}, ${g}, ${b}, ${cfg.aMin})` };

      const ratio = Math.max(0, Math.min(1, v / max));
      const alpha = cfg.aMin + (cfg.aMax - cfg.aMin) * ratio;
      return { backgroundColor: `rgba(${r}, ${g}, ${b}, ${alpha})` };
    },

    isDisplayMetric(metricHeaderKey: string): boolean {
      if (!metricHeaderKey || metricHeaderKey === "metric") return false;
      return !!this.getMetricColorCfgByHeader(metricHeaderKey);
    },

    async rebuild() {
      this.isWidgetLoading = true;
      this.buildHeadersFromProject();
      await this.loadDataFromApi();
      this.isWidgetLoading = false;
    },

    extractScenarioNames(): string[] {
      const proj = cloneDeep(this.projectFiltered);
      if (!proj || !Array.isArray(proj.business_goals)) return [];
      const set = new Set<string>();
      for (const bg of proj.business_goals || []) {
        for (const pl of bg.plans || []) {
          for (const sc of pl.scenarios || []) {
            const name = String(sc?.name ?? "").trim();
            if (name) set.add(name);
          }
        }
      }
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    },

    async loadDataFromApi(): Promise<void> {
      const proj = this.projectFiltered as Project;
      if (!proj || !Array.isArray(proj.business_goals)) {
        this.data = [];
        this.summaryRow = { metric: Lang.total };
        return;
      }

      const scenarioNames = this.extractScenarioNames();

      type SumCell = { sum: number; count: number };
      type AggMap = Record<string, Record<string, SumCell>>;
      type ChannelRow = { row: Record<string, number | string>; agg: AggMap };

      const byChannel: Map<string, ChannelRow> = new Map();
      const summaryAgg: AggMap = {};

      const selectedMetricKeys = this.selectedMetricsSorted.map((m) => m.value);
      const neededMetricKeys = new Set<string>();
      selectedMetricKeys.forEach((metric) => {
        neededMetricKeys.add(metric);
        const w = weightedMap[metric];
        if (w) {
          neededMetricKeys.add(w.num);
          neededMetricKeys.add(w.denom);
        }
      });

      const ensureChannelRow = (channelName: string): ChannelRow => {
        const key = String(channelName || "").trim() || "Unknown";
        let entry = byChannel.get(key);
        if (!entry) {
          const row: Record<string, number | string> = { metric: key };
          const agg: AggMap = {};
          for (const sn of scenarioNames) {
            const sk = scenarioKey(sn);
            agg[sk] = {};
            for (const m of this.selectedMetricsSorted) {
              row[`${sk}_${m.value}`] = 0;
            }
            neededMetricKeys.forEach((metric) => {
              if (!agg[sk][metric]) agg[sk][metric] = { sum: 0, count: 0 };
            });
          }
          entry = { row, agg };
          byChannel.set(key, entry);
        }
        return entry;
      };

      const ensureSummaryAgg = (scenarioKeyLocal: string) => {
        if (!summaryAgg[scenarioKeyLocal]) summaryAgg[scenarioKeyLocal] = {};
        neededMetricKeys.forEach((metric) => {
          if (!summaryAgg[scenarioKeyLocal][metric]) summaryAgg[scenarioKeyLocal][metric] = { sum: 0, count: 0 };
        });
      };

      const NUM = (v: unknown): number => {
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? n : 0;
      };
      const NONEMPTY = (s: unknown): string => String(s ?? "").trim();
      const firstFiniteMetric = (row: Record<string, unknown>, keys: string[]): number | null => {
        for (const key of keys) {
          const raw = row?.[key];
          if (raw === undefined || raw === null || raw === "") continue;
          const value = NUM(raw);
          if (Number.isFinite(value)) return value;
        }
        return null;
      };
      const sumFlightMetric = (r: HeatmapOutputRow, keys: string[]): number => {
        const flights = Array.isArray(r?.flights) ? r.flights : [];
        return flights.reduce((sum, flight) => {
          const value = firstFiniteMetric(flight, keys);
          return sum + (value ?? 0);
        }, 0);
      };
      const metricFromRow = (r: HeatmapOutputRow, metric: string): number => {
        if (metric === "channel_budget") {
          const direct = firstFiniteMetric(r as Record<string, unknown>, [
            "channel_budget",
            "flight_budget",
            "budget",
            "amount",
            "spend",
            "cost",
          ]);
          return direct ?? sumFlightMetric(r, ["channel_budget", "flight_budget", "budget", "amount", "spend", "cost"]);
        }

        if (metric === "impressions") {
          const direct = firstFiniteMetric(r as Record<string, unknown>, ["impressions", "Impressions"]);
          return direct ?? sumFlightMetric(r, ["impressions", "Impressions"]);
        }

        const direct = firstFiniteMetric(r as Record<string, unknown>, [metric]);
        if (direct !== null) return direct;

        return sumFlightMetric(r, [metric]);
      };

      for (const bg of proj.business_goals || []) {
        for (const pl of bg.plans || []) {
          for (const sc of pl.scenarios || []) {
            const sName = NONEMPTY(sc?.name) || "Scenario 1";
            const sKey = scenarioKey(sName);
            const rows = (sc?.output?.data || []) as Arr<HeatmapOutputRow>;
            if (!Array.isArray(rows)) continue;

            ensureSummaryAgg(sKey);
            for (const r of rows) {
              const chName = NONEMPTY(r?.channel) || NONEMPTY(r?.channel_name) || NONEMPTY(r?.Channel) || "Unknown";
              const { agg } = ensureChannelRow(chName);

              neededMetricKeys.forEach((metric) => {
                const v = metricFromRow(r, metric);
                if (Number.isFinite(v)) {
                  agg[sKey][metric].sum += v;
                  agg[sKey][metric].count += 1;
                  summaryAgg[sKey][metric].sum += v;
                  summaryAgg[sKey][metric].count += 1;
                }
              });
            }
          }
        }
      }

      for (const { row, agg } of byChannel.values()) {
        for (const sn of scenarioNames) {
          const sk = scenarioKey(sn);

          for (const m of this.selectedMetricsSorted) {
            const key = m.value;
            const weightedInfo = weightedMap[key];
            if (weightedInfo) {
              const num = agg[sk]?.[weightedInfo.num]?.sum ?? 0;
              const den = agg[sk]?.[weightedInfo.denom]?.sum ?? 0;
              row[`${sk}_${key}`] = den ? ((weightedInfo.multiplier || 1) * num) / den : 0;
            } else {
              const cell = agg[sk][key] || { sum: 0, count: 0 };
              row[`${sk}_${key}`] = m.type === "average" ? (cell.count > 0 ? cell.sum / cell.count : 0) : cell.sum;
            }
          }
        }
      }

      const outRows = Array.from(byChannel.values()).map((v) => v.row);
      outRows.sort((a, b) => String(a.metric).localeCompare(String(b.metric)));
      this.data = outRows as TableDataRow[];

      const summary: Record<string, unknown> = { metric: Lang.total };
      for (const sn of scenarioNames) {
        const sk = scenarioKey(sn);
        for (const m of this.selectedMetricsSorted) {
          const key = m.value;
          const weightedInfo = weightedMap[key];
          if (weightedInfo) {
            const num = summaryAgg[sk]?.[weightedInfo.num]?.sum ?? 0;
            const den = summaryAgg[sk]?.[weightedInfo.denom]?.sum ?? 0;
            summary[`${sk}_${key}`] = den ? ((weightedInfo.multiplier || 1) * num) / den : 0;
          } else {
            const cell = summaryAgg[sk]?.[key] || { sum: 0, count: 0 };
            summary[`${sk}_${key}`] = m.type === "average" ? (cell.count > 0 ? cell.sum / cell.count : 0) : cell.sum;
          }
        }
      }
      this.summaryRow = summary;
    },
    buildHeadersFromProject(): void {
      const headers: TableHeader[] = [];
      headers.push({ text: "Channel", value: "metric", cellClass: "heatmap-table__th-align-left" });

      const scenarioNames = this.extractScenarioNames()
        .slice()
        .sort((a, b) => a.localeCompare(b));

      for (const name of scenarioNames) {
        const sk = scenarioKey(name);
        for (const m of this.selectedMetricsSorted) {
          headers.push({ text: `${name} • ${m.title}`, value: `${sk}_${m.value}` });
        }
      }
      this.headerLabels = headers as TableHeader[];
    },

    async getData(): Promise<void> {
      this.isWidgetLoading = true;
      this.data = [];
      this.isWidgetLoading = false;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme.scss";
@import "@/scss/custom_variables.scss";
.heatmap-table {
  .widget-controls {
    display: flex;
    align-items: center;
  }

  ::v-deep .base-widget__header {
    padding-right: 20px;
  }

  .show-columns-btn {
    background-color: var(--v-medium-base);

    &:hover {
      border: 1px solid rgba(255, 255, 255, 1) !important;
    }
  }
}
::v-deep .table-widget-container table {
  table-layout: fixed;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
::v-deep .table-widget-container th,
.table-widget-container td {
  display: table-cell !important;
  vertical-align: middle;
  white-space: nowrap;
}

::v-deep .table-widget-container tbody tr {
  display: table-row !important;
}
::v-deep .table-widget-container {
  width: 100%;
  overflow-x: hidden;
}

::v-deep .table-widget-container table {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
}

.hm-col-first {
  width: 180px;
}

.hm-col-auto {
  width: 160px;
  max-width: 1px;
}

::v-deep .table-widget-container th,
.table-widget-container td {
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep .v-data-table__wrapper {
  overflow-x: auto;
  position: relative;
}
</style>
<style lang="scss">
@import "@/scss/theme.scss";
.heatmap-table {
  &__th {
    font-size: 14px !important;
  }
  &__group {
    text-align: center !important;
    border-left: 1px solid #ffffff14 !important;
    border-right: 1px solid #ffffff14 !important;
  }
}

.v-application.theme--dark,
.pdf-container.theme--dark {
  .heatmap-table {
    overflow-x: auto;

    &__th-align-right {
      text-align: right !important;
    }

    &__th {
      font-size: 14px;
      white-space: nowrap;
    }
    thead > tr > th {
      color: rgba(255, 255, 255, 0.7);
      background: var(--v-background-dark-base);
    }
    thead > tr > th:hover {
      transition: all linear 0.1s;
      color: var(--v-accent-global-base) !important;
    }

    tr.v-heatmap-table__selected {
      background-color: var(--v-background-dark-base);
    }
    .striped tr:nth-of-type(even) {
      background-color: var(--v-background-light-base);
    }
    tr:hover:not(.v-heatmap-table__expanded__content):not(.v-heatmap-table__empty-wrapper) {
      background-color: transparent !important;
    }
  }
}
.v-application.theme--light,
.pdf-container.theme--light {
  .heatmap-table {
    overflow-x: auto;

    &__th {
      white-space: nowrap;
    }
    .striped tr:nth-of-type(even) {
      background-color: var(--v-content-light-base);
    }
    th:hover {
      transition: all linear 0.1s;
      color: var(--v-accent-global-base) !important;
    }
    tr:hover {
      background-color: var(--v-content-light-base) !important;
    }
  }
}

.hm-col-start {
  border-left: 1px solid #ffffff14 !important;
  box-sizing: border-box;
}
.hm-col-end {
  border-right: 1px solid #ffffff14 !important;
  box-sizing: border-box;
}

.hm-summary-row td {
  font-weight: 600;
  border-top: 1px solid #ffffff1f !important;
}

.hm-summary-label {
  font-weight: 700;
}

.v-application.theme--dark,
.pdf-container.theme--dark {
  .data-table {
    .select.v-input__control {
      border-radius: 4px !important;
    }
    .v-select__selection--comma {
      color: var(--v-content-white-base);
    }
    .v-text-field--filled > .v-input__control > .v-input__slot,
    .v-text-field--full-width > .v-input__control > .v-input__slot,
    .v-text-field--outlined > .v-input__control > .v-input__slot {
      background-color: var(--v-background-medium-base);
    }
    .show-columns-btn {
      background-color: var(--v-medium-base);
    }
  }
}

.heatmap-table {
  thead tr:first-child > th:first-child {
    position: sticky;
    left: 0;
    z-index: 6;
    transform: translateZ(0);
  }

  tbody td:first-child {
    position: sticky;
    left: 0;
    z-index: 3;
  }
}

.v-application.theme--dark,
.pdf-container.theme--dark {
  .heatmap-table {
    tbody td:first-child {
      background: var(--v-background-dark-base) !important;
    }
  }
}

.v-application.theme--light,
.pdf-container.theme--light {
  .heatmap-table {
    tbody td:first-child {
      background: var(--v-content-white-base) !important;
    }
  }
}
</style>
